import React, { useState, useCallback } from 'react';
import { useGlobalCanvas } from '../contexts/GlobalCanvasContext';
import { useKeyboardShortcuts, useKeyboardShortcutsHelp } from '../hooks/useKeyboardShortcuts';
import ResizablePanes from './ResizablePanes';
import { 
  HelpCircle, 
  X, 
  Keyboard
} from 'lucide-react';

import '../styles/obsidian-canvas.css';

interface GlobalCanvasProps {
  onTaskUpdate?: () => void;
  onEmailUpdate?: () => void;
}

// Keyboard Shortcuts Help Component
interface KeyboardHelpProps {
  isVisible: boolean;
  onClose: () => void;
}

const KeyboardHelp: React.FC<KeyboardHelpProps> = ({ isVisible, onClose }) => {
  const { shortcuts } = useKeyboardShortcutsHelp();
  
  if (!isVisible) return null;

  return (
    <div className={`keyboard-hint ${isVisible ? 'show' : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h4>Keyboard Shortcuts</h4>
        <button
          onClick={onClose}
          className="obsidian-tab-action-btn"
          style={{ opacity: 1 }}
          title="Close shortcuts help"
          aria-label="Close shortcuts help"
        >
          <X size={12} />
        </button>
      </div>
      
      {shortcuts.map((category, index) => (
        <div key={index} className="keyboard-hint-category">
          <div className="keyboard-hint-category-title">{category.category}</div>
          <ul>
            {category.items.map((item, itemIndex) => (
              <li key={itemIndex}>
                <div className="keys">
                  {item.keys.map((key, keyIndex) => (
                    <span key={keyIndex} className="key">{key}</span>
                  ))}
                </div>
                <span className="description">{item.description}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

// Main GlobalCanvas component
const GlobalCanvas: React.FC<GlobalCanvasProps> = ({
  onTaskUpdate,
  onEmailUpdate
}) => {
  const { splitViews, tabs, activeTabId, activeSplitView, createCanvas } = useGlobalCanvas();
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [lastShortcutUsed, setLastShortcutUsed] = useState<string | null>(null);

  // Enable keyboard shortcuts with callback for notifications
  useKeyboardShortcuts({ 
    enableGlobalShortcuts: true,
    onShortcutUsed: (shortcut) => {
      console.log('🎹 Shortcut used:', shortcut);
      setLastShortcutUsed(shortcut);
      setTimeout(() => setLastShortcutUsed(null), 2000); // Clear after 2 seconds
    }
  });

  const handleToggleKeyboardHelp = useCallback(() => {
    setShowKeyboardHelp(prev => !prev);
  }, []);

  const handleCreateFirstCanvas = useCallback(() => {
    console.log('🎨 Creating first canvas...');
    createCanvas('Getting Started');
  }, [createCanvas]);

  // Empty state when no split views exist
  if (splitViews.length === 0) {
    return (
      <div className="obsidian-canvas">
        <div className="obsidian-empty-state">
          <div className="obsidian-empty-state-icon">🚀</div>
          <h3 className="obsidian-empty-state-title">Welcome to LexAI Canvas</h3>
          <p className="obsidian-empty-state-description">
            Your powerful workspace for organizing tasks, emails, files, and ideas visually.
          </p>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={handleCreateFirstCanvas}
              className="canvas-tool-btn"
              style={{ 
                padding: '12px 24px', 
                fontSize: '14px',
                background: 'var(--canvas-accent)',
                borderColor: 'var(--canvas-accent)',
                color: 'var(--canvas-text-primary)'
              }}
            >
              🎨 Create Your First Canvas
            </button>
            <button
              onClick={handleToggleKeyboardHelp}
              className="canvas-tool-btn"
              style={{ padding: '12px 24px', fontSize: '14px' }}
            >
              <Keyboard size={16} />
              View Shortcuts
            </button>
          </div>
          <p className="obsidian-empty-state-subtitle">
            Press <kbd>Ctrl+T</kbd> to create a canvas, or click the button above
          </p>
          
          {/* Debug info */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{ 
              marginTop: '2rem', 
              padding: '1rem', 
              background: 'var(--canvas-bg-secondary)', 
              borderRadius: '8px',
              fontSize: '12px',
              color: 'var(--canvas-text-muted)',
              maxWidth: '400px'
            }}>
              <strong>Debug Info:</strong><br />
              Split Views: {splitViews.length}<br />
              Active Split View: {activeSplitView}<br />
              Total Tabs: {tabs?.length || 0}<br />
              Active Tab: {activeTabId || 'None'}<br />
              Main View Tabs: {splitViews.find(v => v.id === 'main')?.tabs.length || 0}<br />
              <em>Try clicking the button or pressing Ctrl+T</em>
            </div>
          )}
          
          {/* Feature highlights */}
          <div className="obsidian-content-grid" style={{ marginTop: '2rem', maxWidth: '600px' }}>
            <div className="obsidian-content-card">
              <div className="obsidian-content-card-label">Drag & Drop</div>
              <div className="obsidian-content-card-value">Reorder tabs and move between panes</div>
            </div>
            <div className="obsidian-content-card">
              <div className="obsidian-content-card-label">Split Views</div>
              <div className="obsidian-content-card-value">Work with multiple content panes</div>
            </div>
            <div className="obsidian-content-card">
              <div className="obsidian-content-card-label">Visual Canvas</div>
              <div className="obsidian-content-card-value">Create mind maps and connect ideas</div>
            </div>
            <div className="obsidian-content-card">
              <div className="obsidian-content-card-label">Pop-out Windows</div>
              <div className="obsidian-content-card-value">Drag tabs out to separate windows</div>
            </div>
          </div>
        </div>
        
        {/* Help overlay */}
        <KeyboardHelp 
          isVisible={showKeyboardHelp} 
          onClose={() => setShowKeyboardHelp(false)} 
        />
      </div>
    );
  }

  console.log('🌍 GlobalCanvas rendering, splitViews:', splitViews.length);

  return (
    <div className="obsidian-canvas">
      {/* Main content with resizable panes */}
      <ResizablePanes
        onUpdateTask={onTaskUpdate}
        onUpdateEmail={onEmailUpdate}
      />
      
      {/* Floating action button for keyboard help */}
      <button
        onClick={handleToggleKeyboardHelp}
        className="canvas-tool-btn"
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 100,
          borderRadius: '50%',
          width: '48px',
          height: '48px',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'var(--canvas-shadow-lg)',
          background: showKeyboardHelp ? 'var(--canvas-accent)' : 'var(--canvas-bg-secondary)'
        }}
        title="Keyboard Shortcuts (Ctrl+Shift+K)"
      >
        <HelpCircle size={20} />
      </button>

      {/* Last shortcut used notification */}
      {lastShortcutUsed && (
        <div 
          className="canvas-tool-btn"
          style={{
            position: 'fixed',
            bottom: '80px',
            left: '20px',
            zIndex: 99,
            pointerEvents: 'none',
            background: 'var(--canvas-success)',
            borderColor: 'var(--canvas-success)',
            color: 'white',
            fontSize: '12px',
            animation: 'obsidian-fade-in 0.2s ease-out'
          }}
        >
          {lastShortcutUsed}
        </div>
      )}
      
      {/* Help overlay */}
      <KeyboardHelp 
        isVisible={showKeyboardHelp} 
        onClose={() => setShowKeyboardHelp(false)} 
      />
    </div>
  );
};

export default GlobalCanvas;